// Import Core
import React from "react";
import { graphql } from "gatsby";
// Import Components
import Seo from "../../../components/Seo";
import ClientTestimonials from "../../../components/blocks/ClientTestimonials";
import ThreeColInfoCard from "../../../components/blocks/ThreeColInfoCard";
// Import Assets
import Stars from "../../../components/ui/Stars";
import styled from "styled-components";
import { useState } from "react";
import { useCallback } from "react";
import useMap from "../../../utils/useMap";
import { useEffect } from "react";
import StickyCta from "../../../components/blocks/ctas/StickyCta";
import FeaturedImageSlider from "../../../components/blocks/FeaturedImageSlider";
import SimpleContent from "../../../components/blocks/SimpleContent";
import AnchorMenu from "../../../components/blocks/AnchorMenu";
import Separator from "../../../components/ui/Separator";
import IconList from "../../../components/ui/IconList";
import { productFeaturesListItems } from "../../../utils/productFeaturesListItems";
import FloatingButton from "../../../components/ui/buttons/FloatingButton";
import MapMultipleLocations from "../../../components/blocks/MapMultipleLocations";
import CardListSimple from "../../../components/blocks/CardListSimple";
import SinglePageFlexibleContent from "../../../components/blocks/SinglePageFlexibleContent";
import CardCarouselProduct from "../../../components/blocks/CardCarouselProduct";
import ProductSpecialOffers from "../../../components/blocks/ProductSpecialOffers";
import Table from "../../../components/blocks/Table";
import MapSlideIn from "../../../components/blocks/MapSlideIn";
// Import Utils
import { numberWithCommas } from "../../../utils/pricing/addCommaThousands";
import { randomExpertInPriority } from "../../../utils/randomExpertInPriority";
import { usePushToDataLayer } from "../../../utils/getPageSanityId";

const ShortBreakStyling = styled.div`
  .short-break-hero {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 80px 0 0;
    text-align: center;
    @media (max-width: 768px) {
      padding: 40px 0 0;
    }
    .tagline {
      color: var(--grey);
    }
    .location {
      color: var(--neutral-75);
    }
    .ratings {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
      .star-rating {
        display: flex;
        .star {
          width: 18.5px;
          height: 17.6px;
        }
      }
      .hotel-type {
        text-transform: capitalize;
      }
    }
  }

  .single-product-content {
    .featured-image-slider {
      padding: unset;
      .gatsby-image-wrapper {
        max-height: 765px;
      }
    }

    .product-content {
    }
  }
`;

const ShortBreakHero = ({ title, location, starRating, hotelType }) => {
  return (
    <div className="short-break-hero">
      <p className="tagline">Short Break</p>
      <h1 className="title red">{title}</h1>
      <h5 className="location">{location}</h5>
      {(starRating || hotelType) && (
        <div className="ratings">
          {starRating && (
            <div className="star-rating">
              <Stars starRating={starRating} />
            </div>
          )}
          {hotelType && hotelType !== "none" && (
            <p className="hotel-type bold italic red">{hotelType}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Define component
const ShortBreakPage = ({ data, location }) => {
  const page = data.shortBreak;
  const price =
    page?.priceFrom?.match(/£([\d,]+)/)?.[1] ||
    page?.seasonPricing?.lowSeasonPrice ||
    page?.seasonPricing?.highSeasonPrice ||
    "NoPrice";
  usePushToDataLayer(price, "short_break", page?._id, "hotel_rental");
  // Check expert collections in priority order
  const expertDetail = randomExpertInPriority(
    page.city?.expertCollection,
    page.expert?.expertCollection,
    page.country?.expertCollection
  );

  const [anchorLinks, setAnchorLinks] = useState([]);
  // SetIsPageLoaded on every render to ensure that if AnchorMenu is true it will render all items
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  useEffect(() => {
    setIsPageLoaded(true);
  });

  // If we have attractions, filter out ones with coordinates to show on the map
  const [mapSelection, setMapSelection] = useState();
  const [ftSliderImages, setFtSliderImages] = useState(null);
  const [seasonPricing, setSeasonPricing] = useState([]);
  const [whatsIncluded, setWhatsIncluded] = useState(null);
  const [threeColInfoCard, setThreeColInfoCard] = useState(null);
  const [priceText, setpriceText] = useState(null);

  useEffect(() => {
    if (data.cities) {
      setMapSelection(
        data.cities.nodes.filter((city) => city.location && city.location.lat)
      );
    }
    // Set images for featuredSlider
    setFtSliderImages(page.images);

    // Set Season Pricing
    const lowPrice =
      page.seasonPricing && page.seasonPricing.lowSeasonPrice
        ? page.seasonPricing.lowSeasonDuration
          ? `${
              page.seasonPricing.lowSeasonDuration
            } nights from £${numberWithCommas(
              page.seasonPricing.lowSeasonPrice
            )} per person`
          : `From £${numberWithCommas(
              page.seasonPricing.lowSeasonPrice
            )} per person`
        : "On Request";
    const highPrice =
      page.seasonPricing && page.seasonPricing.highSeasonPrice
        ? page.seasonPricing.highSeasonDuration
          ? `${
              page.seasonPricing.highSeasonDuration
            } nights from £${numberWithCommas(
              page.seasonPricing.highSeasonPrice
            )} per person`
          : `from £${numberWithCommas(
              page.seasonPricing.highSeasonPrice
            )} per person`
        : "On Request";

    const allYear =
      page.seasonPricing &&
      page.seasonPricing.highSeasonPrice &&
      !page.seasonPricing.lowSeasonDuration
        ? page.seasonPricing.highSeasonDuration
          ? `${
              page.seasonPricing.highSeasonDuration
            } nights from £${numberWithCommas(
              page.seasonPricing.highSeasonPrice
            )} per person`
          : `from £${numberWithCommas(
              page.seasonPricing.highSeasonPrice
            )} per person`
        : "";

    allYear
      ? setSeasonPricing([["All Year", allYear]])
      : setSeasonPricing([
          ["High Season", highPrice],
          ["Low Season", lowPrice],
        ]);

    const priceText =
      highPrice === "On Request" ? (
        ""
      ) : (
        <p>{`Prices are based on two people sharing; they may vary depending on seasonal hotel and flight costs. Please call us for a tailor-made quotation.`}</p>
      );

    setpriceText(priceText);

    // Check if the corresponding country has an includes section, if not then check if it has a city includes section otherwise show product includes section
    if (
      page.country.countryIncludes &&
      page.country.countryIncludes.length > 0
    ) {
      setWhatsIncluded(page.country.countryIncludes);
    } else if (page.city.cityIncludes && page.city.cityIncludes.length > 0) {
      setWhatsIncluded(page.city.cityIncludes);
    } else if (page.includes && page.includes.length > 0) {
      setWhatsIncluded(page.includes);
    }

    if (
      page?.city?.locationPageContent?.locationPageContent &&
      page?.city?.locationPageContent?.locationPageContent.length > 0
    ) {
      setThreeColInfoCard(page.city.locationPageContent.locationPageContent);
    }
  }, [data]);

  return (
    <ShortBreakStyling className="single-product-page">
      <ShortBreakHero
        title={page.title}
        location={`${page.city.name}, ${page.country.name}`}
        starRating={page.starRating}
        hotelType={page.hotelType}
      />

      {ftSliderImages && ftSliderImages.length > 0 && (
        <FeaturedImageSlider
          addToAnchor={false}
          images={page.images}
          title=""
        />
      )}

      {isPageLoaded && (
        <AnchorMenu
          anchorLinks={anchorLinks}
          setAnchorLinks={setAnchorLinks}
          location={location}
        />
      )}

      <div className="single-product-content">
        <div className="product-main">
          <div className="product-content">
            {page.mainContent && (
              <SimpleContent
                content={page.mainContent}
                addToAnchor={true}
                anchorTitle="Overview"
              />
            )}

            {seasonPricing && seasonPricing.length > 0 && (
              <Separator color="grey-thin" />
            )}

            {seasonPricing && seasonPricing.length > 0 && (
              <>
                <Table
                  // title={`Tours with ${page.title}`}
                  // Use index to make key name
                  tableColumns={["", "Holiday Price"]}
                  tableData={seasonPricing}
                  addButton={false}
                />
                {priceText}
              </>
            )}

            {data.specialOffers.nodes &&
              data.specialOffers.nodes.length > 0 && (
                <Separator color="grey-thin" />
              )}

            {data.specialOffers.nodes &&
              data.specialOffers.nodes.length > 0 && (
                <ProductSpecialOffers
                  specialOffers={data.specialOffers.nodes}
                />
              )}

            {whatsIncluded && <Separator color="grey-thin" />}

            {whatsIncluded && (
              <div className="product-features">
                <h3>{`What's included`}</h3>
                <IconList
                  iconListItems={productFeaturesListItems(whatsIncluded)}
                  iconLayout="minimal"
                  showDescriptions={true}
                />
              </div>
            )}
          </div>

          {/* STICKY CTA WITH COUNTRY EXPERT */}
          {expertDetail && (
            <StickyCta
              country={page.country.name}
              expert={expertDetail}
              pageType="product"
              productTitle={page.title}
              productDarwinId={page.darwinId}
            />
          )}
        </div>
        <div className="threeColInfo">
          {threeColInfoCard &&
            threeColInfoCard.map((el) => {
              if (el._type === "threeColInfoCard") {
                return (
                  <ThreeColInfoCard
                    addToAnchor={el.addToAnchor}
                    leftContent={el.leftContent}
                    middleContent={el.middleContent}
                    navItem={el.navItem}
                    image={el.image}
                  />
                );
              }
            })}
        </div>
      </div>

      {page.singlePageContent &&
        page.singlePageContent.singlePageContent &&
        page.singlePageContent.singlePageContent.length > 0 && (
          <Separator color="grey-thin" />
        )}
      {page.singlePageContent &&
        page.singlePageContent.singlePageContent &&
        page.singlePageContent.singlePageContent.length > 0 && (
          <SinglePageFlexibleContent
            pageContent={page.singlePageContent.singlePageContent}
          />
        )}

      {page.location &&
        page.location.lat &&
        mapSelection &&
        mapSelection.length > 0 && <Separator color="grey-thick" />}

      {/* FOLLOW UP: MAP - EXPLORE LOCAL AREA - WILL PROB NEED GEOLIB? NO CLEAR DIRECTION GIVEN */}
      {page.location &&
        page.location.lat &&
        mapSelection &&
        mapSelection.length > 0 && (
          <MapSlideIn
            className="explore"
            mapSelection={mapSelection}
            allLocations={data.cities.nodes}
            tagline="Destination"
          />
        )}

      {data.hotels.nodes && data.hotels.nodes.length > 0 && (
        <Separator color="red" />
      )}

      {/* // TODO: LINK TO SEARCH PAGE!!!!!!!!!!!!!! */}
      {data.hotels.nodes && data.hotels.nodes.length > 0 && (
        <CardCarouselProduct
          productType={page._type}
          addToAnchor="false"
          title={`More hotels in ${page.country.name}`}
          content=""
          // linkText="LINK TO SEARCH PAGE!!!!"
          // link="/short-breaks"
          cardList={data.hotels.nodes.slice(0, 10)}
          linkText={"View All"}
          link={`/short-breaks/?${
            page.pageContentInfo?.pageType === "city" ? "cities" : "countries"
          }=${page.country.name.toLowerCase()}`}
        />
      )}
      {data.tailorMadeHolidays.nodes &&
        data.tailorMadeHolidays.nodes.length > 0 && (
          <CardCarouselProduct
            productType="tailorMadeHoliday"
            addToAnchor="false"
            title={`tailor-made holidays in ${page.country.name}`}
            content={""}
            cardList={data.tailorMadeHolidays.nodes.slice(0, 10)}
            linkText={"View All"}
            link={`/tailor-made-holidays/tours/?${
              page.pageContentInfo?.pageType === "city" ? "cities" : "countries"
            }=${page.country.name.toLowerCase()}`}
          />
        )}
      {data.culturalTours.nodes && data.culturalTours.nodes.length > 0 && (
        <CardCarouselProduct
          productType="culturalTour"
          addToAnchor="false"
          title={`cultural Tours in ${page.country.name}`}
          content={""}
          cardList={data.culturalTours.nodes.slice(0, 10)}
          linkText={"View All"}
          link={`/cultural-tours-and-music-holidays/tours/?${
            page.pageContentInfo?.pageType === "city" ? "cities" : "countries"
          }=${page.country.name.toLowerCase()}`}
        />
      )}

      {data.testimonials?.nodes?.length > 0 && (
        <ClientTestimonials
          addToAnchor={true}
          testimonials={data.testimonials.nodes}
        />
      )}
    </ShortBreakStyling>
  );
};

// Attach SEO
export const Head = ({ data }) => {
  const page = data.shortBreak;
  return (
    <Seo
      title={
        page.seoGroup && page.seoGroup.seoMetaTitle
          ? page.seoGroup.seoMetaTitle
          : `${page.name} - Kirker Holidays - Short Break`
      }
      description={
        page.seoGroup &&
        page.seoGroup.seoMetaDescription &&
        page.seoGroup.seoMetaDescription
      }
      key={page._id}
    />
  );
};

// Export component
export default ShortBreakPage;

// Add page query
export const query = graphql`
  query (
    $slug: String!
    $pageLocationId: String!
    $pageId: String!
    $slugRegex: String!
  ) {
    tailorMadeHolidays: allSanityTailorMadeHoliday(
      filter: {
        includedCountries: { elemMatch: { _id: { eq: $pageLocationId } } }
      }
    ) {
      nodes {
        ...SanityTailorMadeHolidayFragment
      }
    }
    culturalTours: allSanityCulturalTour(
      filter: {
        includedCountries: { elemMatch: { _id: { eq: $pageLocationId } } }
      }
    ) {
      nodes {
        ...SanityCulturalTourFragment
      }
    }
    shortBreak: sanityHotelBreak(slug: { current: { eq: $slug } }) {
      _id
      _type
      title
      slug {
        current
      }
      darwinId
      priceFrom
      addAnchor
      enabled
      highlyRecommended
      # thumbnail {
      # 	asset {
      # 		gatsbyImageData(width: 400, placeholder: BLURRED)
      # 	}
      # }
      mainContent: _rawMainContent(resolveReferences: { maxDepth: 10 })
      images {
        asset {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
      seasonPricing {
        lowSeasonDuration
        lowSeasonPrice
        highSeasonDuration
        highSeasonPrice
      }
      starRating
      hotelType

      # excerpt

      includes {
        name
        description
        icon {
          _type
          name
          provider
        }
      }
      location {
        alt
        lat
        lng
      }

      # themeTags {
      #   themeTags {
      #     tag {
      #       label
      #       value
      #     }
      #     _type
      #   }
      #   _type
      # }
      city {
        _id
        _type
        name
        slug {
          current
        }
        cityIncludes: includes {
          name
          description
          icon {
            _type
            name
            provider
            svg
          }
        }
        country {
          _id
          _type
          isInEurope
          slug {
            current
          }
        }
        expertCollection {
          _id
          _type
          title
          jobTitle
          email
          thumbnail {
            asset {
              gatsbyImageData(width: 80, height: 80, placeholder: BLURRED)
            }
          }
          slug {
            current
          }
        }
        locationPageContent {
          locationPageContent {
            ...SanityThreeColInfoCardFragment
          }
        }
      }
      region {
        _id
        _type
        name
        slug {
          current
        }
        country {
          _id
          _type
          isInEurope
          slug {
            current
          }
        }
        expertCollection {
          _id
          _type
          title
          jobTitle
          email
          thumbnail {
            asset {
              gatsbyImageData(width: 80, height: 80, placeholder: BLURRED)
            }
          }
          slug {
            current
          }
        }
      }
      country {
        _id
        _type
        name
        slug {
          current
        }
        isInEurope
        expertCollection {
          _id
          _type
          title
          jobTitle
          email
          thumbnail {
            asset {
              gatsbyImageData(width: 80, height: 80, placeholder: BLURRED)
            }
          }
          slug {
            current
          }
        }

        countryIncludes: includes {
          name
          description
          icon {
            _type
            name
            provider
            svg
          }
        }
      }

      # Flexible Content
      singlePageContent {
        ...SanitySinglePageContentFragment
      }
      seoGroup {
        seoMetaTitle
        seoMetaDescription
      }
    }

    cities: allSanityCityPage(
      filter: { country: { _id: { eq: $pageLocationId } } }
    ) {
      nodes {
        _id
        _type
        name
        image: thumbnail {
          asset {
            gatsbyImageData(width: 100, height: 100, placeholder: BLURRED)
          }
        }
        slug {
          current
        }
        country {
          _id
          _type
          name
          isInEurope
          slug {
            current
          }
        }
        location {
          lat
          lng
        }
      }
    }

    specialOffers: allSanitySpecialOffer(
      filter: {
        assignedCptType: { eq: "hotelBreak" }
        assignedCptHotelBreak: { _id: { eq: $pageId } }
        enabled: { eq: true }
      }
    ) {
      nodes {
        _id
        _type
        title
        excerpt
        assignedCptType
      }
    }

    hotels: allSanityHotelBreak(
      limit: 10
      filter: { country: { _id: { eq: $pageLocationId } } }
    ) {
      nodes {
        _id
        _type
        images {
          asset {
            gatsbyImageData(height: 800, placeholder: BLURRED)
          }
        }
        thumbnail {
          asset {
            gatsbyImageData(height: 800, placeholder: BLURRED)
          }
        }
        title
        city {
          name
        }
        country {
          _id
          _type
          name
          isInEurope
          slug {
            current
          }
        }
        starRating
        hotelType
        highlyRecommended
        excerpt
        includes {
          _type
          name
          icon {
            name
            provider
          }
        }
        slug {
          current
        }
      }
    }

    testimonials: allSanityTestimonial(
      filter: {
        enabled: { eq: true }
        slug: { current: { regex: $slugRegex } }
      }
    ) {
      nodes {
        id
        excerpt
        isGeneric
        _rawMainContent
        # publishedDate(formatString: "DD MMM YYYY")
        author
      }
    }
  }
`;
